import { FormattedMessage } from '@alltrails/shared/react-intl';
import LanguageRegionCode from '@alltrails/shared/types/LanguageRegionCode';
import { wrapUrlSafe } from '@alltrails/shared/utils/languageSupport';
import PlaceTrail from '@alltrails/shared/icons/PlaceTrail';
import Community from '@alltrails/shared/icons/Community';
import NationalPark from '@alltrails/shared/icons/NationalPark';
import Heart from '@alltrails/shared/icons/Heart';
import PartyPopper from '@alltrails/shared/icons/PartyPopper';
import Time from '@alltrails/shared/icons/Time';
import { User } from 'types/User';
import Shop from '@alltrails/shared/icons/Shop';
import ArrowExternal from '@alltrails/shared/icons/ArrowExternal';
import DrawPath from '@alltrails/shared/icons/DrawPath';
import List from '@alltrails/shared/icons/List';
import WebHeaderSection from '@alltrails/analytics/enums/WebHeaderSection';
import WebHeaderDropDownLink from '@alltrails/analytics/enums/WebHeaderDropDownLink';
import { ReactNode } from 'react';
import Calendar from '@alltrails/shared/icons/Calendar';
import { AuthRequiredHeaderLink, HeaderLink } from './headerLinks';
import plansMenuBadgeSrc from './menuBadges/plans-menu-badge.svg';
import peakMenuBadgeSrc from './menuBadges/peak-menu-badge.svg';

export type HeaderSection = {
  label: ReactNode;
  links: HeaderLink[];
  testId: string;
  webHeaderSection: WebHeaderSection;
};

type GetHeaderLinksArgs = {
  includeInvites: boolean;
  includeRecentlyViewed: boolean;
  inUS: boolean;
  languageRegionCode: LanguageRegionCode;
  pathname?: string;
  user: User | null;
};

export const getHeaderLinks = ({
  includeInvites,
  includeRecentlyViewed,
  inUS,
  languageRegionCode,
  pathname,
  user
}: GetHeaderLinksArgs): HeaderSection[] => {
  const recentlyViewedLink: AuthRequiredHeaderLink = {
    leadingIcon: { background: true, icon: { Component: Time } },
    getLinkInfo: () => ({ href: wrapUrlSafe('/explore/recently-viewed', languageRegionCode) }),
    requiresAuth: true,
    testId: 'header-recently-viewed',
    title: <FormattedMessage defaultMessage="Recently viewed" />,
    webHeaderDropDownLink: WebHeaderDropDownLink.RecentlyViewed
  };
  const exploreSection: HeaderSection = {
    label: <FormattedMessage defaultMessage="Explore" />,
    links: [
      {
        leadingIcon: { background: true, icon: { Component: PlaceTrail } },
        linkInfo: { href: wrapUrlSafe('/explore', languageRegionCode) },
        testId: 'header-nearby-trails',
        title: <FormattedMessage defaultMessage="Nearby trails" />,
        webHeaderDropDownLink: WebHeaderDropDownLink.NearbyTrails
      },
      ...(includeRecentlyViewed ? [recentlyViewedLink] : []),
      {
        leadingIcon: { background: true, icon: { Component: DrawPath } },
        // 'trigger=nav' is used to pass the correct trigger and upsell trigger to the SignUpModal
        // 'returnTo' is used to pass the current pathname to the SignUpModal so that the user can
        // be redirected back to the current page if they don't authenticate
        linkInfo: { href: wrapUrlSafe(`/explore/custom-routes/new?trigger=nav${pathname ? `&returnTo=${pathname}` : ''}`, languageRegionCode) },
        testId: 'header-build-custom-route',
        title: <FormattedMessage defaultMessage="Build custom route" />,
        webHeaderDropDownLink: WebHeaderDropDownLink.CustomRoute
      },
      {
        leadingIcon: { background: true, icon: { Component: Community } },
        linkInfo: { href: wrapUrlSafe('/community', languageRegionCode) },
        testId: 'header-community',
        title: <FormattedMessage defaultMessage="Community" />,
        webHeaderDropDownLink: WebHeaderDropDownLink.Community
      },
      {
        leadingIcon: { background: true, icon: { Component: NationalPark } },
        linkInfo: { href: wrapUrlSafe('/guides', languageRegionCode) },
        testId: 'header-national-park-guides',
        title: <FormattedMessage defaultMessage="National park guides" />,
        webHeaderDropDownLink: WebHeaderDropDownLink.NationalParkGuides
      }
    ],
    testId: 'header-explore',
    webHeaderSection: WebHeaderSection.Explore
  };

  const createListLink: HeaderLink = {
    leadingIcon: { background: true, icon: { Component: List } },
    testId: 'header-create-list',
    title: <FormattedMessage defaultMessage="Create a list" />,
    requiresAuth: true,
    getLinkInfo: ({ slug }) => ({
      href: wrapUrlSafe(`/members/${slug}/lists`, languageRegionCode)
    }),
    webHeaderDropDownLink: WebHeaderDropDownLink.CreateAList
  };

  const byotSavedUnauthenticatedLinks: HeaderLink[] = [
    {
      leadingIcon: { background: true, icon: { Component: DrawPath } },
      // 'returnTo' is used to navigate the user to their custom routes after they sign up / sign in
      linkInfo: {
        href: wrapUrlSafe(`/signup?returnTo=${encodeURIComponent('/explore?user-custom-routes=true')}`, languageRegionCode)
      },
      testId: 'header-custom-routes',
      title: <FormattedMessage defaultMessage="Custom routes & maps" />,
      webHeaderDropDownLink: WebHeaderDropDownLink.CustomRoute
    },
    createListLink
  ];

  const unauthenticatedSavedLinks: HeaderLink[] = byotSavedUnauthenticatedLinks;

  const savedLinks: HeaderLink[] = user
    ? [
        {
          leadingIcon: { background: true, icon: { Component: Heart } },
          linkInfo: { href: wrapUrlSafe(`/members/${user.slug}/favorites`, languageRegionCode) },
          testId: 'header-favorites',
          title: <FormattedMessage defaultMessage="Favorites" />,
          webHeaderDropDownLink: WebHeaderDropDownLink.Favorites
        },
        {
          leadingIcon: { background: true, icon: { Component: DrawPath } },
          linkInfo: { href: wrapUrlSafe(`/members/${user.slug}/custom-routes`, languageRegionCode) },
          testId: 'header-custom-routes',
          title: <FormattedMessage defaultMessage="Custom routes & maps" />,
          webHeaderDropDownLink: WebHeaderDropDownLink.CustomRoute
        },
        {
          leadingIcon: { background: true, icon: { Component: List } },
          linkInfo: { href: wrapUrlSafe(`/members/${user.slug}/lists`, languageRegionCode) },
          testId: 'header-lists',
          title: <FormattedMessage defaultMessage="Lists" />,
          webHeaderDropDownLink: WebHeaderDropDownLink.ViewAllLists
        },
        ...(includeInvites
          ? [
              {
                leadingIcon: { background: true, icon: { Component: Calendar } },
                linkInfo: { href: wrapUrlSafe(`/members/${user.slug}/invites`, languageRegionCode) },
                testId: 'header-invites',
                title: 'Invites', // TODO: EVENT TRANSLATIONS (DISCO-24049)
                webHeaderDropDownLink: WebHeaderDropDownLink.Invites
              }
            ]
          : [])
      ]
    : unauthenticatedSavedLinks;
  const savedSection: HeaderSection = {
    label: <FormattedMessage defaultMessage="Saved" />,
    links: savedLinks,
    testId: 'header-saved',
    webHeaderSection: WebHeaderSection.Save
  };

  const shopLinks: HeaderLink[] = [];

  if (!user || user.subscriptionTier === 'base') {
    shopLinks.push({
      leadingIcon: { customElement: <img src={plansMenuBadgeSrc} alt="" /> },
      linkInfo: { href: wrapUrlSafe('/plans', languageRegionCode) },
      testId: 'header-alltrails-plans',
      title:
        !user || user.isPromoEligible ? (
          <FormattedMessage defaultMessage="Try Plus for free" />
        ) : (
          <FormattedMessage defaultMessage="Upgrade to Plus" />
        ),
      webHeaderDropDownLink: WebHeaderDropDownLink.AtPlus
    });
  }

  if (user?.subscriptionTier === 'plus') {
    shopLinks.push({
      leadingIcon: { customElement: <img src={peakMenuBadgeSrc} alt="" /> },
      linkInfo: { href: wrapUrlSafe('/plans', languageRegionCode) },
      testId: 'header-alltrails-peak',
      title: <FormattedMessage defaultMessage="Try Peak" />,
      webHeaderDropDownLink: WebHeaderDropDownLink.AtPlus
    });
  }

  shopLinks.push({
    leadingIcon: { background: true, icon: { Component: PartyPopper } },
    linkInfo: { href: wrapUrlSafe('/gift', languageRegionCode) },
    testId: 'header-give-alltrails-plus',
    title: <FormattedMessage defaultMessage="Gift membership" />,
    webHeaderDropDownLink: WebHeaderDropDownLink.GiveAtPlus
  });

  if (inUS) {
    shopLinks.push({
      leadingIcon: { background: true, icon: { Component: Shop } },
      linkInfo: { href: 'https://shop.alltrails.com' },
      testId: 'header-shop',
      title: 'Gear Shop', // This is intentionally untranslated (DISCO-10275)
      trailingIcon: { icon: { Component: ArrowExternal, color: '--color-text-secondary', size: 'xs' } },
      webHeaderDropDownLink: WebHeaderDropDownLink.GearShop
    });
  }
  const shopSection: HeaderSection = {
    label: <FormattedMessage defaultMessage="Shop" />,
    links: shopLinks,
    testId: 'header-shop',
    webHeaderSection: WebHeaderSection.Shop
  };

  return [exploreSection, savedSection, shopSection];
};
